/**
 * Detail collapse for a specialty.
 */
import * as React from 'react';
import { Star, Calendar, Info, Layers } from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailsCard } from '@/components/common/DetailsCard';
import {
  SPECIALTY_STATUS_CONFIG,
  getSpecialtyStatusColor,
} from '@/components/specialties/specialty-configs';
import type { Specialty } from '@/types/specialty.types';

/** Detail collapse sections for a specialty (header + levels / status / metadata). */
export const renderSpecialtyDetails = (specialty: Specialty): React.ReactNode => {
  const statusKey = specialty.isActive ? 'active' : 'inactive';
  const statusConfig = SPECIALTY_STATUS_CONFIG[statusKey];
  const StatusIcon = statusConfig.icon;

  const levels = specialty.levelLinks?.map((ll) => ll.level) ?? [];

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Star className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{specialty.label}</h3>
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getSpecialtyStatusColor(specialty.isActive)}
                variant="filled"
              />
            </div>
            <p className="details-view-header-subtitle">{specialty.code}</p>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={Info} title="Informations générales" colorName="color-2" colSpan={2}>
          <div className="space-y-3">
            <DetailField label="Code" value={specialty.code} />
            <DetailField label="Libellé" value={specialty.label} />
            {specialty.sortOrder !== undefined && (
              <DetailField label="Ordre d'affichage" value={String(specialty.sortOrder)} />
            )}
          </div>
        </DetailsCard>

        {levels.length > 0 && (
          <DetailsCard icon={Layers} title="Niveaux associés" colorName="color-1" colSpan={1}>
            <div className="space-y-2">
              {levels.map((level) => (
                <div
                  key={level.id}
                  className="flex items-center gap-2 rounded-lg border border-[color-mix(in_srgb,var(--color-1-nuance-3)_30%,transparent)] px-3 py-1.5"
                >
                  <Layers className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
                  <span className="text-sm font-medium text-foreground">{level.label}</span>
                  <span className="ml-auto font-mono text-xs text-muted-foreground">
                    {level.code}
                  </span>
                </div>
              ))}
            </div>
          </DetailsCard>
        )}

        {levels.length === 0 && (
          <DetailsCard icon={Calendar} title="Niveaux associés" colorName="color-1" colSpan={1}>
            <p className="text-sm text-muted-foreground">Aucun niveau associé</p>
          </DetailsCard>
        )}
      </div>
    </>
  );
};
